import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = []
  static values = {
    patientId: Number,
    appointmentId: Number
  }

  connect() {
    this.searchTimeout = null
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  handleClickOutside(event) {
    const searchContainer = document.querySelector('.search-container')
    if (searchContainer && !searchContainer.contains(event.target)) {
      this.hideSearchResults()
    }
  }

  async toggleDislike(event) {
    const checkbox = event.target
    const componentId = checkbox.dataset.componentId
    const productId = checkbox.dataset.productId
    const componentName = checkbox.dataset.componentName
    const productName = checkbox.dataset.productName
    const isDisliked = checkbox.checked

    const itemId = componentId || productId
    const itemName = componentName || productName
    const itemType = componentId ? 'Component' : 'Product'

    // Mostrar mensaje de estado
    this.showStatusMessage(`Procesando ${itemName}...`, 'info')

    try {
      if (isDisliked) {
        // Crear dislike
        await this.createDislike(itemId, itemName, itemType)
      } else {
        // Eliminar dislike
        await this.removeDislike(itemId, itemName, itemType)
      }
    } catch (error) {
      console.error('Error toggling dislike:', error)
      // Revertir el estado del checkbox
      checkbox.checked = !isDisliked
      this.showStatusMessage(`Error al procesar ${itemName}`, 'danger')
    }
  }

  async excludeWithRecipes(event) {
    const button = event.currentTarget
    const componentId = button.dataset.componentId
    const componentName = button.dataset.componentName

    // Deshabilitar botón mientras se procesa
    button.disabled = true
    this.showStatusMessage(`Excluyendo ${componentName} y recetas asociadas...`, 'info')

    try {
      const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          food_dislike: {
            dislikeable_type: 'Component',
            dislikeable_id: componentId
          },
          exclude_also_products_with_core_component: true,
          redirect_to: 'food_dislikes'
        })
      })

      if (response.ok) {
        // Marcar el checkbox como checked
        const checkbox = document.querySelector(`input[data-component-id="${componentId}"]`)
        if (checkbox) {
          checkbox.checked = true
        }
        this.showStatusMessage(`${componentName} y recetas asociadas excluidas exitosamente`, 'success')
        this.addBadgeToSummary(componentName)
        // Recargar la página para actualizar la lista de recetas
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        throw new Error('Failed to exclude with recipes')
      }
    } catch (error) {
      console.error('Error excluding with recipes:', error)
      this.showStatusMessage(`Error al excluir ${componentName} y recetas asociadas`, 'danger')
    } finally {
      button.disabled = false
    }
  }

  async createDislike(itemId, itemName, itemType) {
    const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        food_dislike: {
          dislikeable_type: itemType,
          dislikeable_id: itemId
        },
        redirect_to: 'food_dislikes'
      })
    })

    if (response.ok) {
      this.showStatusMessage(`${itemName} agregado a la lista de excluidos`, 'success')
      this.addBadgeToSummary(itemName, itemType)
    } else {
      throw new Error('Failed to create dislike')
    }
  }

  async removeDislike(itemId, itemName, itemType) {
    // Verificar si el item tiene patologías activas (solo para componentes)
    if (itemType === 'Component') {
      const checkbox = document.querySelector(`input[data-component-id="${itemId}"]`)
      if (checkbox && checkbox.dataset.hasPathology === 'true') {
        // Verificar si realmente tiene patologías activas
        const componentElement = checkbox.closest('.food-item-toggle')
        if (componentElement && componentElement.classList.contains('has-pathology')) {
          throw new Error('No se puede incluir un ingrediente que está restringido por una patología activa')
        }
      }
    }

    // Primero necesitamos encontrar el food_dislike_id
    const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Failed to fetch food dislikes')
    }

    const data = await response.json()
    const foodDislike = data.find(fd =>
      fd.dislikeable_type === itemType &&
      fd.dislikeable_id == itemId
    )

    if (!foodDislike) {
      throw new Error('Food dislike not found')
    }

    const deleteResponse = await fetch(`/food_dislikes/${foodDislike.id}?redirect_to=food_dislikes`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })

    if (deleteResponse.ok) {
      const responseData = await deleteResponse.json().catch(() => ({}))
      if (responseData.success === false && responseData.error) {
        throw new Error(responseData.error)
      }
      this.showStatusMessage(`${itemName} eliminado de la lista de excluidos`, 'success')
      this.removeBadgeFromSummary(itemName)
    } else {
      const errorData = await deleteResponse.json().catch(() => ({}))
      if (errorData.error) {
        throw new Error(errorData.error)
      }
      throw new Error('Failed to delete dislike')
    }
  }

  async togglePathology(event) {
    const checkbox = event.target
    const pathologyId = checkbox.dataset.pathologyId
    const pathologyName = checkbox.dataset.pathologyName
    const isActive = checkbox.checked

    // Mostrar mensaje de estado
    this.showStatusMessage(`Procesando patología ${pathologyName}...`, 'info')

    try {
      if (isActive) {
        // Activar patología
        await this.activatePathology(pathologyId, pathologyName)
      } else {
        // Desactivar patología
        await this.deactivatePathology(pathologyId, pathologyName)
      }
    } catch (error) {
      console.error('Error toggling pathology:', error)
      // Revertir el estado del checkbox
      checkbox.checked = !isActive
      this.showStatusMessage(`Error al procesar ${pathologyName}: ${error.message}`, 'danger')
    }
  }

  async activatePathology(pathologyId, pathologyName) {
    const response = await fetch(`/patients/${this.patientIdValue}/user_diet_restrictions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        user_diet_restriction: {
          user_id: this.patientIdValue,
          diet_restriction_id: pathologyId
        }
      })
    })

    if (response.ok) {
      this.showStatusMessage(`Patología ${pathologyName} activada. Los ingredientes relacionados han sido excluidos automáticamente.`, 'success')
      // Recargar la página para actualizar los estados de los ingredientes
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to activate pathology')
    }
  }

  async toggleRegimen(event) {
    const checkbox = event.target
    const regimenId = checkbox.dataset.regimenId
    const regimenName = checkbox.dataset.regimenName
    const regimenComponents = checkbox.dataset.regimenComponents || ''
    const isActive = checkbox.checked

    // Mostrar popup de confirmación
    const action = isActive ? 'activar' : 'desactivar'
    const consequence = isActive
      ? 'Se agregarán como excluidos los siguientes ingredientes (y sus recetas asociadas)'
      : 'Se eliminarán de excluidos los siguientes ingredientes (y sus recetas asociadas)'
    const componentsList = regimenComponents || 'Sin componentes definidos'

    if (!confirm(`¿Desea ${action} el régimen "${regimenName}"?\n\n${consequence}:\n${componentsList}`)) {
      checkbox.checked = !isActive
      return
    }

    this.showStatusMessage(`Procesando régimen ${regimenName}...`, 'info')

    try {
      if (isActive) {
        await this.activateRegimen(regimenId, regimenName)
      } else {
        await this.deactivateRegimen(checkbox, regimenId, regimenName)
      }
    } catch (error) {
      console.error('Error toggling regimen:', error)
      checkbox.checked = !isActive
      this.showStatusMessage(`Error al procesar ${regimenName}: ${error.message}`, 'danger')
    }
  }

  async activateRegimen(regimenId, regimenName) {
    const response = await fetch(`/patients/${this.patientIdValue}/user_diet_regimens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        user_diet_regimen: {
          user_id: this.patientIdValue,
          diet_regimen_id: regimenId
        }
      })
    })

    if (response.ok) {
      this.showStatusMessage(`Régimen ${regimenName} activado. Los ingredientes relacionados han sido excluidos automáticamente.`, 'success')
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to activate regimen')
    }
  }

  async deactivateRegimen(checkbox, regimenId, regimenName) {
    const userDietRegimenId = checkbox.dataset.userDietRegimenId

    if (!userDietRegimenId) {
      throw new Error('No se encontró el ID del régimen activo')
    }

    const response = await fetch(`/patients/${this.patientIdValue}/user_diet_regimens/${userDietRegimenId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      }
    })

    if (response.ok) {
      this.showStatusMessage(`Régimen ${regimenName} desactivado. Los ingredientes relacionados han sido incluidos nuevamente.`, 'success')
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to deactivate regimen')
    }
  }

  async deactivatePathology(pathologyId, pathologyName) {
    // Buscar el user_diet_restriction en la página actual
    const pathologyElement = document.querySelector(`[data-pathology-id="${pathologyId}"]`)
    if (!pathologyElement) {
      throw new Error('Pathology element not found')
    }

    // Buscar el link de delete en la página (si existe en otro lugar) o usar el endpoint directamente
    // Intentar encontrar el user_diet_restriction_id desde el DOM o hacer una petición
    const deleteLink = document.querySelector(`[data-diet-restriction-id="${pathologyId}"] a[data-method="delete"]`)

    let userDietRestrictionId = null
    if (deleteLink) {
      const href = deleteLink.getAttribute('href')
      const match = href.match(/\/user_diet_restrictions\/(\d+)/)
      if (match) {
        userDietRestrictionId = match[1]
      }
    }

    // Si no encontramos el ID en el DOM, necesitamos buscarlo
    if (!userDietRestrictionId) {
      // Hacer una petición para obtener el user_diet_restriction_id
      // Usaremos el endpoint de user_diet_restrictions con el patient_id
      const indexResponse = await fetch(`/patients/${this.patientIdValue}/user_diet_restrictions`, {
        method: 'GET',
        headers: {
          'Accept': 'text/html'
        }
      })

      if (indexResponse.ok) {
        const html = await indexResponse.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const foundDeleteLink = doc.querySelector(`[data-diet-restriction-id="${pathologyId}"] a[data-method="delete"]`)

        if (foundDeleteLink) {
          const href = foundDeleteLink.getAttribute('href')
          const match = href.match(/\/user_diet_restrictions\/(\d+)/)
          if (match) {
            userDietRestrictionId = match[1]
          }
        }
      }
    }

    if (!userDietRestrictionId) {
      throw new Error('Could not find user diet restriction ID')
    }

    // Eliminar la patología
    const destroyResponse = await fetch(`/patients/${this.patientIdValue}/user_diet_restrictions/${userDietRestrictionId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (destroyResponse.ok) {
      this.showStatusMessage(`Patología ${pathologyName} desactivada.`, 'success')
      // Recargar la página para actualizar los estados
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    } else {
      const errorData = await destroyResponse.json().catch(() => ({}))
      throw new Error(errorData.error || 'Failed to deactivate pathology')
    }
  }

  showStatusMessage(message, type) {
    const messageElement = document.getElementById('toggle-status-message')
    const messageText = document.getElementById('toggle-message-text')

    if (messageElement && messageText) {
      messageText.textContent = message
      messageElement.className = `alert alert-${type}`
      messageElement.classList.remove('d-none')

      // Ocultar el mensaje después de un tiempo
      const timeout = type === 'info' ? 5000 : 4000
      setTimeout(() => {
        messageElement.classList.add('d-none')
      }, timeout)
    }
  }

  addBadgeToSummary(itemName, itemType = 'Component', dislikeId = null, dislikeIdValue = null) {
    const summaryContainer = document.querySelector('.food-preferences-summary')
    if (summaryContainer) {
      // Verificar si ya existe el badge
      const existingBadge = Array.from(summaryContainer.querySelectorAll('.food-dislike-badge')).find(
        badge => {
          const badgeText = badge.cloneNode(true)
          badgeText.querySelector('button').remove()
          return badgeText.textContent.trim() === itemName
        }
      )
      if (existingBadge) return

      const badge = document.createElement('span')
      badge.className = 'badge px-3 mr-2 mb-2 rounded-pill d-inline-flex align-items-center food-dislike-badge'

      // Diferentes colores para ingredientes y recetas
      if (itemType === 'Component') {
        // Ingredientes: fondo rojo oscuro, texto blanco
        badge.style.backgroundColor = '#8B0000'
        badge.style.color = 'white'
        badge.style.border = '2px solid #8B0000'
      } else {
        // Recetas: fondo blanco, texto rojo oscuro, borde rojo oscuro
        badge.style.backgroundColor = 'white'
        badge.style.color = '#8B0000'
        badge.style.border = '2px solid #8B0000'
      }

      // Agregar data attributes
      if (dislikeId) badge.dataset.dislikeId = dislikeId
      badge.dataset.dislikeType = itemType
      if (dislikeIdValue) badge.dataset.dislikeIdValue = dislikeIdValue
      badge.dataset.dislikeName = itemName

      badge.innerHTML = `${itemName} <button type="button" class="food-dislike-remove-btn" data-action="click->food-preference-toggles-unified#removeFromSummary" title="Incluir (eliminar de excluidos)"><i class="fas fa-times"></i></button>`
      summaryContainer.appendChild(badge)
    }
  }

  async removeFromSummary(event) {
    event.stopPropagation()
    const button = event.currentTarget
    const badge = button.closest('.food-dislike-badge')
    const dislikeId = badge.dataset.dislikeId
    const dislikeType = badge.dataset.dislikeType
    const dislikeIdValue = badge.dataset.dislikeIdValue
    // Extraer el nombre del badge (todo el texto excepto el botón)
    const badgeText = badge.cloneNode(true)
    badgeText.querySelector('button').remove()
    const dislikeName = badgeText.textContent.trim()

    // Confirmación antes de eliminar
    if (!confirm(`¿Está seguro que desea incluir "${dislikeName}" nuevamente?`)) {
      return
    }

    try {
      // Deshabilitar el botón mientras se procesa
      button.disabled = true
      this.showStatusMessage(`Incluyendo ${dislikeName}...`, 'info')

      // Buscar el food_dislike_id si no está disponible
      let foodDislikeId = dislikeId
      if (!foodDislikeId) {
        const response = await fetch(`/patients/${this.patientIdValue}/food_dislikes`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          const foodDislike = data.find(fd =>
            fd.dislikeable_type === dislikeType &&
            fd.dislikeable_id == dislikeIdValue
          )
          if (foodDislike) {
            foodDislikeId = foodDislike.id
          }
        }
      }

      if (!foodDislikeId) {
        throw new Error('Food dislike not found')
      }

      // Eliminar el dislike
      const deleteResponse = await fetch(`/food_dislikes/${foodDislikeId}?redirect_to=food_dislikes`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (deleteResponse.ok) {
        const responseData = await deleteResponse.json().catch(() => ({}))
        if (responseData.success === false && responseData.error) {
          throw new Error(responseData.error)
        }

        // Remover el badge del resumen
        badge.remove()

        // Actualizar el toggle correspondiente
        if (dislikeType === 'Component') {
          const checkbox = document.querySelector(`input[data-component-id="${dislikeIdValue}"]`)
          if (checkbox) {
            checkbox.checked = false
          }
        } else {
          const checkbox = document.querySelector(`input[data-product-id="${dislikeIdValue}"]`)
          if (checkbox) {
            checkbox.checked = false
          }
        }

        this.showStatusMessage(`${dislikeName} incluido exitosamente`, 'success')
      } else {
        const errorData = await deleteResponse.json().catch(() => ({}))
        if (errorData.error) {
          throw new Error(errorData.error)
        }
        throw new Error('Failed to delete dislike')
      }
    } catch (error) {
      console.error('Error removing from summary:', error)
      this.showStatusMessage(`Error al incluir ${dislikeName}`, 'danger')
      button.disabled = false
    }
  }

  removeBadgeFromSummary(itemName) {
    const summaryContainer = document.querySelector('.food-preferences-summary')
    if (summaryContainer) {
      const badges = summaryContainer.querySelectorAll('.badge')
      badges.forEach(badge => {
        if (badge.textContent.includes(itemName)) {
          badge.remove()
        }
      })
    }
  }

  searchFoods(event) {
    const searchTerm = event.target.value.toLowerCase().trim()

    // Limpiar timeout anterior
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (searchTerm === '') {
      this.clearSearchResults()
      this.hideSearchResults()
      this.hideSpinner()
      return
    }

    // Mostrar spinner mientras espera el debounce
    this.showSpinner()

    // Debounce: esperar 300ms después de que el usuario deje de escribir
    this.searchTimeout = setTimeout(() => {
      this.performSearch(searchTerm)
    }, 300)
  }

  async performSearch(searchTerm) {
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length >= 2)

    if (searchWords.length === 0) {
      this.hideSpinner()
      this.hideSearchResults()
      return
    }

    try {
      const patientId = this.patientIdValue
      const response = await fetch(`/patients/${patientId}/food_dislikes/search?query=${encodeURIComponent(searchTerm)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) throw new Error('Search request failed')

      const data = await response.json()

      const results = []

      data.ingredients.forEach(item => {
        results.push({
          id: item.id,
          name: item.name,
          type: 'component',
          itemType: 'Component',
          isAlreadyDisliked: item.isAlreadyDisliked
        })
      })

      data.recipes.forEach(item => {
        results.push({
          id: item.id,
          name: item.name,
          type: 'product',
          itemType: 'Product',
          isAlreadyDisliked: item.isAlreadyDisliked
        })
      })

      this.hideSpinner()
      this.renderSearchResults(results, searchTerm)
    } catch (error) {
      console.error('Search error:', error)
      this.hideSpinner()
      this.renderSearchResults([], searchTerm)
    }
  }

  renderSearchResults(results, searchTerm) {
    const dropdown = document.getElementById('search-results-dropdown')
    if (!dropdown) return

    if (results.length === 0) {
      dropdown.innerHTML = `<div class="search-results-empty">No se encontraron resultados para "${searchTerm}"</div>`
      dropdown.classList.remove('d-none')
      return
    }

    // Separar ingredientes y recetas
    const ingredients = results.filter(r => r.type === 'component')
    const recipes = results.filter(r => r.type === 'product')

    let html = ''

    if (ingredients.length > 0) {
      html += '<div class="search-results-section">'
      html += `<div class="search-results-section-title"><i class="fas fa-carrot mr-1"></i> Ingredientes (${ingredients.length})</div>`
      ingredients.forEach(item => {
        const disabledClass = item.isAlreadyDisliked ? 'search-result-disabled' : ''
        const icon = item.isAlreadyDisliked ? '<i class="fas fa-check-circle text-success mr-2"></i>' : '<i class="fas fa-plus-circle text-danger mr-2"></i>'
        const label = item.isAlreadyDisliked ? '<small class="text-muted ml-auto">ya excluido</small>' : ''
        html += `<div class="search-result-item ${disabledClass}" data-action="click->food-preference-toggles-unified#addDislikeFromSearch" data-search-item-id="${item.id}" data-search-item-name="${item.name}" data-search-item-type="${item.itemType}" data-already-disliked="${item.isAlreadyDisliked}">${icon}<span>${item.name}</span>${label}</div>`
      })
      html += '</div>'
    }

    if (recipes.length > 0) {
      html += '<div class="search-results-section">'
      html += `<div class="search-results-section-title"><i class="fas fa-utensils mr-1"></i> Recetas (${recipes.length})</div>`
      recipes.forEach(item => {
        const disabledClass = item.isAlreadyDisliked ? 'search-result-disabled' : ''
        const icon = item.isAlreadyDisliked ? '<i class="fas fa-check-circle text-success mr-2"></i>' : '<i class="fas fa-plus-circle text-danger mr-2"></i>'
        const label = item.isAlreadyDisliked ? '<small class="text-muted ml-auto">ya excluido</small>' : ''
        html += `<div class="search-result-item ${disabledClass}" data-action="click->food-preference-toggles-unified#addDislikeFromSearch" data-search-item-id="${item.id}" data-search-item-name="${item.name}" data-search-item-type="${item.itemType}" data-already-disliked="${item.isAlreadyDisliked}">${icon}<span>${item.name}</span>${label}</div>`
      })
      html += '</div>'
    }

    dropdown.innerHTML = html
    dropdown.classList.remove('d-none')
  }

  async addDislikeFromSearch(event) {
    const el = event.currentTarget
    const itemId = el.dataset.searchItemId
    const itemName = el.dataset.searchItemName
    const itemType = el.dataset.searchItemType
    const alreadyDisliked = el.dataset.alreadyDisliked === 'true'

    if (alreadyDisliked) return

    // Deshabilitar el elemento mientras se procesa
    el.classList.add('search-result-saving')
    el.querySelector('i').className = 'fas fa-spinner fa-spin mr-2'

    try {
      await this.createDislike(itemId, itemName, itemType)

      // Marcar el checkbox correspondiente en los tabs
      const selector = itemType === 'Component'
        ? `input[data-component-id="${itemId}"]`
        : `input[data-product-id="${itemId}"]`
      const checkbox = document.querySelector(selector)
      if (checkbox) checkbox.checked = true

      // Actualizar el resultado en el dropdown
      el.classList.remove('search-result-saving')
      el.classList.add('search-result-disabled')
      el.dataset.alreadyDisliked = 'true'
      el.querySelector('i').className = 'fas fa-check-circle text-success mr-2'
      // Agregar label "ya excluido" si no existe
      if (!el.querySelector('small')) {
        el.insertAdjacentHTML('beforeend', '<small class="text-muted ml-auto">ya excluido</small>')
      }
    } catch (error) {
      console.error('Error adding dislike from search:', error)
      el.classList.remove('search-result-saving')
      el.querySelector('i').className = 'fas fa-plus-circle text-danger mr-2'
      this.showStatusMessage(`Error al excluir ${itemName}`, 'danger')
    }
  }

  hideSearchResults() {
    const dropdown = document.getElementById('search-results-dropdown')
    if (dropdown) {
      dropdown.classList.add('d-none')
      dropdown.innerHTML = ''
    }
  }

  showSpinner() {
    const spinner = document.getElementById('search-spinner')
    if (spinner) {
      spinner.classList.remove('d-none')
    }
  }

  hideSpinner() {
    const spinner = document.getElementById('search-spinner')
    if (spinner) {
      spinner.classList.add('d-none')
    }
  }

  clearSearchResults() {
    const foodItems = document.querySelectorAll('.food-item-toggle')
    foodItems.forEach(item => {
      item.classList.remove('search-highlight')
      const foodNameElement = item.querySelector('.food-item-name')
      if (foodNameElement) {
        foodNameElement.classList.remove('search-matched')
      }
    })
  }
}
